import { useState, SyntheticEvent, useMemo, useEffect, useCallback } from 'react';

// MUI Imports
import { Tabs, Tab, Box, useTheme } from '@mui/material';

// Library Imports
import { IInteractionRpc } from '@pulse-ui/codegen/generated_ts/managed_algo';

// Component Imports
import BotDetailsTab from '../BotDetailsTab/BotDetailsTab';
import { RpcTab } from '../RpcTab/RpcTab';

// Hook Imports
import { useRpcFormState } from '../../hooks';

// Utils Imports
import {
    getDefaultRpcEntries,
    isRpcDisabledForStatus,
    type DefaultSlot,
} from '../../utils';

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export type TabEntry = | { type: "details", id: string, label: string } | {
    type: "rpc",
    connectionId: string;
    interaction: IInteractionRpc;
    rpcName: string;
    rpcSchema: any;
    formKey: string;
    slot: DefaultSlot;
};

export interface VerticalTabsProps {
    interaction: Record<string, IInteractionRpc>;
    selectedBotData: any;
    connectionId: string | null;
    initialRpcName?: string | null;
    selectedRowStatus?: string | null;
    onRpcSuccess?: () => void;
}

/**
 * Vertical tabs component that manages RPC forms.
 * Supports multiple connections: one tab per (connection, RPC).
 * Form state is keyed by connectionId + rpcName so each tab has independent state.
 * Connection/session for submit is derived from which form is submitted (that tab's interaction).
 */
export default function VerticalTabs({
    interaction,
    selectedBotData,
    connectionId,
    initialRpcName,
    selectedRowStatus,
    onRpcSuccess = () => { }
}: VerticalTabsProps) {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const { getFormData, setFormData } = useRpcFormState();

    // One tab per (connectionId, rpc). When connectionId prop is set, only show RPCs for that connection.
    const tabEntries = useMemo((): TabEntry[] => {
        if (!interaction) return [];
        const entries: TabEntry[] = [];

        if (selectedBotData) {
            entries.push({ type: "details", id: "details", label: "Details" });
        }

        for (const [connId, interactionValue] of Object.entries(interaction)) {
            if (connectionId != null && connId !== connectionId) continue;

            // Use only default RPCs for both start and interactions flows
            const defaultEntries = getDefaultRpcEntries(interactionValue);

            for (const { slot, rpcName, schema: rpcSchema } of defaultEntries) {
                const ui = rpcSchema?.ui as { instance_request?: boolean } | undefined;

                if (connectionId != null) {
                    // InteractionsModal: include all default RPCs except ones explicitly opt-out via instance_request === false
                    if (ui?.instance_request === false) continue;
                } else {
                    // StartStrategyModal: only the "start" default slot
                    if (slot !== "start") continue;
                }

                entries.push({
                    type: "rpc",
                    connectionId: connId,
                    interaction: interactionValue,
                    rpcName,        // actual backend RPC name: start_bot / submit_simple_spread / etc
                    rpcSchema,
                    formKey: `${connId}-${rpcName}`,
                    slot,
                });
            }
        }

        return entries;
    }, [interaction, selectedBotData, connectionId]);

    // When modal opens with a specific RPC, select that tab
    useEffect(() => {
        if (initialRpcName == null || tabEntries.length === 0) return;
        const idx = tabEntries.findIndex(
            (e) => e.type === "rpc" && e.rpcName === initialRpcName
        );
        if (idx >= 0) setTabValue(idx);
    }, [initialRpcName, tabEntries]);

    const handleChange = (_: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const activeEntry = tabEntries[tabValue];

    // Stable callback — recreated only when the active form key changes (i.e. tab switch).
    // Equality check prevents re-storing identical data and breaking the JsonForms onChange loop.
    const activeFormKey = activeEntry?.type === "rpc" ? activeEntry.formKey : null;
    const handleFormDataChange = useCallback((data: any) => {
        if (!activeFormKey) return;
        const current = getFormData(activeFormKey);
        if (current !== data && JSON.stringify(current) !== JSON.stringify(data)) {
            setFormData(activeFormKey, data);
        }
    }, [activeFormKey, getFormData, setFormData]);

    if (tabEntries.length === 0) {
        return (
            <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', height: '100%' }}>
                <div className="pulse-typography-body-1 pulse-color--background-100">
                    No RPCs available
                </div>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', height: '100%', boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.75)' }}>
            <Tabs
                orientation="vertical"
                variant="scrollable"
                value={tabValue}
                onChange={handleChange}
                sx={{
                    flex: '0 0 auto',
                    borderRight: 1,
                    borderColor: 'divider',
                    height: '100%',
                    backgroundColor: theme.palette.bg[800],
                    '& .MuiTabs-indicator': {
                        backgroundColor: (theme.palette.secondary as any)["200"],
                        width: '2px'
                    },
                    minWidth: '250px',
                    '& .MuiTab-root': {
                        minWidth: 'auto',
                        width: '100%',
                        textAlign: 'left',
                        textTransform: 'none',
                        alignItems: 'flex-start',
                        paddingLeft: '16px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }
                }}
            >
                {tabEntries.map((entry, index) => {
                    const isRpcTabDisabled =
                        entry.type === "rpc" &&
                        isRpcDisabledForStatus(entry.slot, selectedRowStatus ?? undefined);
                    return (
                        <Tab
                            key={entry.type === "details" ? entry.id : entry.formKey}
                            label={entry.type === "details" ? entry.label : (entry.rpcSchema?.ui?.display_name || entry.rpcName)}
                            disabled={isRpcTabDisabled}
                            {...a11yProps(index)}
                            sx={{
                                color: theme.palette.bg[50],
                                '&.Mui-selected': {
                                    color: (theme.palette.secondary as any)["200"]
                                },
                                '&.Mui-disabled': {
                                    color: (theme.palette.bg as any)["300"],
                                }
                            }}
                        />
                    );
                })}
            </Tabs>

            {activeEntry && (
                activeEntry.type === "details" ? (
                    <BotDetailsTab data={selectedBotData} />
                ) : (
                    <RpcTab
                        key={activeEntry.formKey}
                        rpcName={activeEntry.rpcName}
                        rpcSchema={activeEntry.rpcSchema}
                        interaction={activeEntry.interaction}
                        value={tabValue}
                        index={tabValue}
                        formData={getFormData(activeEntry.formKey)}
                        selectedBotData={selectedBotData}
                        onFormDataChange={handleFormDataChange}
                        onSubmitConfig={{
                            showSuccess: true,
                            showError: true,
                            resetFormOnSuccess: false,
                            onSuccess: onRpcSuccess,
                            ...(activeEntry.rpcName === 'submit_simple_spread' && {
                                resetFormOnSuccess: true,
                            }),
                        }}
                    />
                ))}
        </Box>
    );
}